import React from 'react';
import {IconButton, Link, Typography, useBreakpointDown} from '@bonprix/design-system';
import ProductCardHeader from './ProductCardHeader.jsx';
import ProductCardFeature from './ProductCardFeature.jsx';
import ProductCardImageSlider from './ProductCardImageSlider.jsx';
import ProductCardSingleImage from './ProductCardSingleImage.jsx';
import WishlistHeart from '../WishlistHeart.jsx';
import {
    NEITHER_IN_TAB_NOR_IN_ARROW_ORDER,
    NOT_IN_TAB_BUT_IN_ARROW_ORDER,
    useGroupedFocus
} from '../../hooks/useGroupedFocus';

export const Touchpoint = {
    WISHLIST: 'wishlist',
    RECOMMENDATION: 'recommendation',
    LSA: 'last-seen-articles',
    ADD_TO_CART_SHEET: 'add-to-cart-sheet'
};

const ProductCard = ({
                         item,
                         texts,
                         touchpoint,
                         isSoldOut,
                         onItemClick,
                         handleSelectSizeClick,
                         children
                     }) => {

    const isMobile = useBreakpointDown("l");
    const focusGroupRef = useGroupedFocus();

    const keyDownHandler = (event) => {
        if (event.key === 'Enter' && document.activeElement?.classList.contains('product-card')) {
            onItemClick?.();
            window.location.href = item.productLink;
        }
    }

    return (
        <div className={'product-card'} tabIndex={0} ref={focusGroupRef} onKeyDown={keyDownHandler}>
            <div className={`product-card_content ${touchpoint}-touchpoint`}>
                <div className='product-card_image-wrapper'>
                    <div className={isSoldOut ? 'product-card_sold-out' : null}>
                        <a href={item.productLink} onClick={onItemClick} className='product-card_image-link' tabIndex={NEITHER_IN_TAB_NOR_IN_ARROW_ORDER}>
                            {touchpoint === Touchpoint.WISHLIST ?
                                (
                                    <ProductCardImageSlider
                                        touchpoint={touchpoint}
                                        item={item}
                                    />
                                )
                                : (
                                    <ProductCardSingleImage item={item} />
                                )
                            }
                        </a>
                    </div>
                    {isSoldOut && (
                        <div className='product-card_sold-out-text'>
                            <Typography variant='h6' component='div'>{texts.pc_soldout}</Typography>
                        </div>
                    )}
                </div>
                {((touchpoint === Touchpoint.WISHLIST && !isMobile) || touchpoint === Touchpoint.RECOMMENDATION) &&
                    item.wishlistLandmarkData &&
                    <div className="product-card_on-image-button">
                        <WishlistHeart
                            heartVariant="tertiary"
                            productCardItem={item}
                        />
                    </div>
                }

                <div className={`product-card_details-area ${touchpoint}-touchpoint`}>
                    <div>
                        <ProductCardHeader item={item} texts={texts} onItemClick={onItemClick} touchpoint={touchpoint} isMobile={isMobile} />
                        {touchpoint === Touchpoint.WISHLIST || touchpoint === Touchpoint.ADD_TO_CART_SHEET ? (
                            <ProductCardFeature
                                item={item}
                                texts={texts}
                                touchpoint={touchpoint}
                                handleSelectSizeClick={handleSelectSizeClick}
                            />
                        ) : null
                        }

                    </div>
                    {(touchpoint === Touchpoint.WISHLIST || touchpoint === Touchpoint.LSA) ? (
                        <div className={'product-card_actions'}>
                            <div className={'product-card_buttonbar'}>
                                {React.Children.map(children, (child) => {
                                    if (React.isValidElement(child) && child.type === ProductCard.PrimaryButton) {
                                        return React.cloneElement(child);
                                    }
                                    return null;
                                })}
                                {isMobile &&
                                    item.wishlistLandmarkData &&
                                    <WishlistHeart
                                        heartVariant="secondary"
                                        productCardItem={item}
                                    />
                                }
                            </div>
                        </div>
                    ) : null
                    }
                </div>
            </div>
        </div>
    );
};

const PrimaryButton = ({icon, arialabel, linktext, clickHandler}) => {
    const keyDownHandler = (event) => {
        if (event.key === 'Enter') {
            const button = event.currentTarget.querySelector(".product-card_buttonbar button");
            if (button) button.click();
        }
    }

    return (
        <div className='product-card_primary_button-with-text' onKeyDown={keyDownHandler}
             tabIndex={NOT_IN_TAB_BUT_IN_ARROW_ORDER}>
            {linktext && (
                <Link
                    button
                    small
                    tabIndex={NEITHER_IN_TAB_NOR_IN_ARROW_ORDER}
                    href={null}
                    onClick={clickHandler}
                >
                    {linktext}
                </Link>
            )}
            <IconButton
                onClick={clickHandler}
                variant='primary'
                icon={icon}
                aria-label={arialabel}
                label={arialabel}
                tabIndex={NEITHER_IN_TAB_NOR_IN_ARROW_ORDER}
            />
        </div>
    );
};

ProductCard.PrimaryButton = PrimaryButton;
export default ProductCard;
